import { supabase } from "@/integrations/supabase/client";

export interface LearningObjective {
  id: string;
  certificationId: string;
  chapterId: string;
  code: string;
  kLevel: string;
  description: string;
  status: "active" | "draft";
  order?: number;
}

type Listener = () => void;
let _items: LearningObjective[] = [];
let _loaded = false;
const listeners = new Set<Listener>();
const notify = () => listeners.forEach((fn) => fn());

export function rowToLO(r: any): LearningObjective {
  return {
    id: r.lo_id,
    certificationId: r.certification_id,
    chapterId: r.chapter_id,
    code: r.code,
    kLevel: r.k_level,
    description: r.description,
    status: r.status as "active" | "draft",
    order: r.sort_order ?? 0,
  };
}

async function loadFromDb() {
  const { data } = await supabase.from("learning_objectives").select("*").order("sort_order");
  _items = (data ?? []).map(rowToLO);
  _loaded = true;
  notify();
}

loadFromDb();

export const learningObjectiveStore = {
  get: () => _items,
  subscribe: (fn: Listener) => { listeners.add(fn); return () => listeners.delete(fn); },
  isLoaded: () => _loaded,
  reload: loadFromDb,

  async add(item: LearningObjective) {
    if (item.order === undefined) {
      const siblings = _items.filter(
        (lo) => lo.certificationId === item.certificationId && lo.chapterId === item.chapterId
      );
      item.order = siblings.length > 0 ? Math.max(...siblings.map((s) => s.order ?? 0)) + 1 : 1;
    }
    const { error } = await supabase.from("learning_objectives").insert({
      lo_id: item.id,
      certification_id: item.certificationId,
      chapter_id: item.chapterId,
      code: item.code,
      k_level: item.kLevel,
      description: item.description,
      status: item.status,
      sort_order: item.order,
    });
    if (!error) {
      _items = [..._items, item];
      notify();
    }
  },

  async update(id: string, patch: Partial<LearningObjective>) {
    const dbPatch: Record<string, unknown> = {};
    if (patch.certificationId !== undefined) dbPatch.certification_id = patch.certificationId;
    if (patch.chapterId !== undefined) dbPatch.chapter_id = patch.chapterId;
    if (patch.code !== undefined) dbPatch.code = patch.code;
    if (patch.kLevel !== undefined) dbPatch.k_level = patch.kLevel;
    if (patch.description !== undefined) dbPatch.description = patch.description;
    if (patch.status !== undefined) dbPatch.status = patch.status;
    if (patch.order !== undefined) dbPatch.sort_order = patch.order;

    const { error } = await supabase.from("learning_objectives").update(dbPatch).eq("lo_id", id);
    if (!error) {
      _items = _items.map((lo) => (lo.id === id ? { ...lo, ...patch } : lo));
      notify();
    }
  },

  async remove(id: string) {
    const { error } = await supabase.from("learning_objectives").delete().eq("lo_id", id);
    if (!error) {
      _items = _items.filter((lo) => lo.id !== id);
      notify();
    }
  },

  async reorderByIds(certificationId: string, chapterId: string, orderedIds: string[]) {
    const siblings = _items.filter(
      (lo) => lo.certificationId === certificationId && lo.chapterId === chapterId
    );
    if (siblings.length !== orderedIds.length) return;
    const sorted = [...siblings].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const orders = sorted.map((s, i) => s.order ?? i + 1);
    const idToOrder = new Map(orderedIds.map((id, i) => [id, orders[i]]));
    _items = _items.map((lo) => {
      const newOrder = idToOrder.get(lo.id);
      return newOrder !== undefined ? { ...lo, order: newOrder } : lo;
    });
    notify();
    for (const [loId, order] of idToOrder) {
      await supabase.from("learning_objectives").update({ sort_order: order }).eq("lo_id", loId);
    }
  },
};
